import { FormikProps } from 'formik';
// mantine
import {
    NumberInput,
    Select,
    JsonInput,
    TagsInput,
    Textarea
} from '@mantine/core';
// components
import type { TAttributeData } from '@newo/api/gateway';
import JSONEdit from '@/components/JSONEdit';
// utils

interface AttributeValueProps {
    formik: FormikProps<TAttributeData>;
    isInline?: boolean;
}

const AttributeValue = ({ formik, isInline = false }: AttributeValueProps) => {
    const handleValueTypeChange = (valueType: string | null) => {
        if (valueType) {
            formik.setFieldValue('value_type', valueType);

            switch (valueType) {
                case 'enum': {
                    formik.setFieldValue(
                        'value',
                        formik.values.possible_values[0]
                    );
                    break;
                }
                case 'bool': {
                    formik.setFieldValue('value', 'True');
                    break;
                }
                case 'number': {
                    formik.setFieldValue('value', 0);
                    break;
                }
                case 'json': {
                    formik.setFieldValue('value', JSON.stringify({}));
                    break;
                }
                default: {
                    formik.setFieldValue('value', '');
                }
            }
        }
    };

    const getNumberValue = () => {
        return +formik.values.value;
    };

    // eslint-disable-next-line sonarjs/function-return-type
    const getJsonValue = () => {
        try {
            return JSON.parse(formik.values.value) as Record<string, unknown>;
        } catch (error) {
            console.error(error);
            return formik.values.value;
        }
    };

    const handleValueChange = (value: unknown) => {
        switch (formik.values.value_type) {
            case 'number': {
                formik.setFieldValue('value', `${value as number}`);
                break;
            }
            case 'json': {
                if (isInline) {
                    formik.setFieldValue('value', value);
                    return;
                }
                formik.setFieldValue('value', JSON.stringify(value));
                break;
            }
            default: {
                formik.setFieldValue('value', value);
            }
        }
    };

    const handlePossibleValuesChange = (values: string[]) => {
        formik.setFieldValue('possible_values', values);
    };

    return (
        <>
            {isInline ? undefined : (
                <Select
                    data={['string', 'enum', 'bool', 'number', 'json']}
                    label="Value type"
                    name="value_type"
                    value={formik.values.value_type}
                    onChange={handleValueTypeChange}
                />
            )}
            {formik.values.value_type === 'enum' && !isInline ? (
                <TagsInput
                    withAsterisk
                    error={
                        formik.touched.possible_values &&
                        formik.errors.possible_values
                            ? formik.errors.possible_values
                            : undefined
                    }
                    label="Possible values"
                    value={formik.values.possible_values}
                    onChange={handlePossibleValuesChange}
                />
            ) : undefined}
            <div>
                {isInline ? undefined : (
                    <div className="text-xs font-semibold">
                        Value
                        {formik.values.value_type === 'enum' ? (
                            <span className="text-xs text-red-6">&nbsp; *</span>
                        ) : undefined}
                    </div>
                )}
                {formik.values.value_type === 'enum' ? (
                    <Select
                        data={formik.values.possible_values}
                        error={
                            formik.touched.value && formik.errors.value
                                ? formik.errors.value
                                : undefined
                        }
                        name="value"
                        value={formik.values.value}
                        onChange={handleValueChange}
                    />
                ) : undefined}
                {formik.values.value_type === 'string' ? (
                    <Textarea
                        autosize
                        error={
                            formik.touched.value && formik.values.value
                                ? formik.errors.value
                                : undefined
                        }
                        maxRows={40}
                        minRows={3}
                        name="value"
                        placeholder="Value"
                        value={formik.values.value}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                    />
                ) : undefined}
                {formik.values.value_type === 'bool' ? (
                    <Select
                        data={['True', 'False']}
                        name="value"
                        value={formik.values.value}
                        onChange={handleValueChange}
                    />
                ) : undefined}
                {formik.values.value_type === 'number' ? (
                    <NumberInput
                        error={
                            formik.touched.value && formik.values.value
                                ? formik.errors.value
                                : undefined
                        }
                        value={getNumberValue()}
                        onChange={handleValueChange}
                    />
                ) : undefined}
                {formik.values.value_type === 'json' ? (
                    <div>
                        {isInline ? (
                            <JsonInput
                                autosize
                                error={formik.errors.value}
                                maxRows={40}
                                minRows={3}
                                value={formik.values.value}
                                onChange={handleValueChange}
                            />
                        ) : (
                            <JSONEdit
                                data={getJsonValue()}
                                setData={handleValueChange}
                            />
                        )}
                    </div>
                ) : undefined}
            </div>
        </>
    );
};

export default AttributeValue;
